/*
 * @Description:
 * @Author: Jack Deng
 * @Date: 2021-07-01 20:34:47
 * @LastEditors: Teemo Ding
 * @LastEditTime: 2022-06-29 11:46:50
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelCurrency/index.tsx
 */
import { SectionWrapper } from '@/src/components';
import { Checkbox, Select } from '@/src/components/Antd';
import ItemWrapper from '@/src/components/Layouts/Item/Item';
import Link from '@/src/components/Link/Link';
import NoPermission from '@/src/components/NoPermission';
import GoBackAndBreadCrumb from '@/src/layout/EditorPageLayout/GoBackAndBreadCrumb/GoBackAndBreadCrumb';
import TopRightBtnsGroups from '@/src/layout/EditorPageLayout/TopRightBtnsGroups/TopRightBtnsGroups';
import useBreadCrumbData from '@/src/pages/SalesCannelVersion/Hooks/useBreadCrumbData';
import {
  isUserValid,
  salesChannelResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import { Icon } from '@/src/tools/icon';
import { getI18nFromGeneral } from '@/src/tools/tools';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { ConfigProvider, Popover, Radio, Space } from 'antd';
import classnames from 'classnames';
import React from 'react';
import RouteBlock from 'src/constrains/RouterBlock/RouterBlock';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet, replaceI18nVariables } from 'src/tools/tools';
import {
  Cnversion,
  DecimalSeparator,
  DisplayFormat,
  ThousandsSeparator,
  conversionOptions,
  decimalPositionOptions,
  ruleStartPosition,
} from './constant';
import useService from './service';
import styles from './style.module.less';

const permissions = (uuid: string) => {
  const view_currency_setting = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelCurrency,
        operation: EnumOperation.view_currency_setting,
        uuid,
      }),
    ],
  });
  const edit_currency_settings = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelCurrency,
        operation: EnumOperation.edit_currency_settings,
        uuid,
      }),
    ],
  });

  return {
    view_currency_setting,
    edit_currency_settings,
  };
};

function SalesChannelCurrency() {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const {
    navigateBack,
    handleSubmit,
    handleDiscard,
    handleChangePropsViaEvent,
    handleChangePropsViaValue,
    handleChangeCurrency,
    handleChangeShowSecond,
    handleChangeSecondCurrency,
    handleChangeDecimalPosition,
    getOptions,
    //reportingCurrency,
    currencyConfig,
    saveDisabled,
    salesChannelUuid,
    primaryCuSymbol,
    secondCuSymbol,
    nonEditable,
  } = useService(permissions);
  const { breadcrumbData } = useBreadCrumbData({
    lastSecond: getI18n('general.sider.settings'),
    lastOne: getI18n('general.currency'),
  });
  const { view_currency_setting, edit_currency_settings } =
    permissions(salesChannelUuid);

  const labelify = (arr: Array<string>) =>
    arr.map((a) => ({ label: a, value: a }));

  const formatExample = (type: DisplayFormat, symbol: string) => {
    switch (type) {
      case DisplayFormat.PREFIX_AMOUNT:
        return `${symbol} 1,245.00`;
      case DisplayFormat.AMOUNT_SUFFIX:
        return `1,245.00 ${symbol}`;
      default:
        return;
    }
  };

  const renderRoundingExample = () => {
    const { roundingRule, decimalPosition } = currencyConfig;
    const position = ruleStartPosition(roundingRule);
    return `${replaceI18nVariables(
      getI18n('salesChannelCurrency.index.roundingof'),
      'HKD',
      '1,245.7892',
      'HKD',
    )}
    ${(
      Math.floor(1245.7892 * Math.pow(10, position)) / Math.pow(10, position)
    ).toFixed(decimalPosition)}`;
  };

  const renderTruncatingExample = () => {
    const { truncateRule, decimalPosition } = currencyConfig;
    const position = ruleStartPosition(truncateRule) || 0;
    return `   ${replaceI18nVariables(
      getI18n('salesChannelCurrency.index.roundingof'),
      'CNY',
      '1,245.7892',
      'CNY',
    )}
    ${(
      Math.trunc(1245.7892 * Math.pow(10, position)) / Math.pow(10, position)
    ).toFixed(decimalPosition)}`;
  };

  return view_currency_setting ? (
    <ConfigProvider
      getPopupContainer={(trigger) => {
        return trigger
          ? trigger.parentElement
          : document.getElementById('salesChannelCurrency');
      }}>
      <div className={styles.container} id='salesChannelCurrency'>
        <div className={styles.header}>
          <GoBackAndBreadCrumb
            goBack={navigateBack}
            breadcrumbData={breadcrumbData}
          />
          <TopRightBtnsGroups
            onSubmit={handleSubmit}
            onDiscard={handleDiscard}
            onSaveDisable={saveDisabled}
          />
        </div>
        <div className={styles.content}>
          <SectionWrapper
            className={styles.wrapper}
            title={
              <div className={styles.title}>
                <span>
                  {getI18n('salesChannelCurrency.index.salesChannelCurrency')}
                </span>
                <Popover title={null} content={<Hint />} placement={'rightTop'}>
                  <Icon type='icontooltip' className={styles.icon} />
                </Popover>
              </div>
            }>
            <div className={styles.warpperBody}>
              <ItemWrapper
                title={getI18n('salesChannelCurrency.index.primaryCurrency')}>
                <Select
                  labelInValue
                  className={styles.select}
                  size='large'
                  value={{
                    label: currencyConfig.currencyName,
                    value: currencyConfig.currencyCode,
                  }}
                  onChange={handleChangeCurrency}
                  disabled={!edit_currency_settings || nonEditable}
                  placeholder={getI18n(
                    'salesChannelCurrency.index.selectcurrency',
                  )}
                  options={getOptions(true).map((c) => ({
                    label: c.reportingCurrency
                      ? `${c.label} - ${getI18n(
                          'salesChannelCurrency.index.reportingcurrency',
                        )}`
                      : c.label,
                    value: c.code,
                  }))}
                />
              </ItemWrapper>
              <Nav className={styles.wrapperNav} />
            </div>
          </SectionWrapper>
          <SectionWrapper
            className={styles.wrapper}
            title={getI18n(
              'salesChannelCurrency.index.secondaryCurrencyDisplay',
            )}>
            <div className={styles.warpperBody}>
              <ItemWrapper>
                <Checkbox
                  disabled={!edit_currency_settings}
                  checked={currencyConfig.showSecondary}
                  onChange={handleChangeShowSecond}>
                  {getI18n(
                    'salesChannelCurrency.index.secondaryCurrencyDisplayCheckbox',
                  )}
                </Checkbox>
                {currencyConfig.showSecondary && (
                  <Select
                    className={styles.seconarySelect}
                    labelInValue
                    onChange={handleChangeSecondCurrency}
                    placeholder={getI18n(
                      'salesChannelCurrency.index.selectcurrency',
                    )}
                    options={getOptions(false).map((c) => ({
                      label: c.reportingCurrency
                        ? `${c.label} - ${getI18n(
                            'salesChannelCurrency.index.reportingcurrency',
                          )}`
                        : c.label,
                      value: c.code,
                    }))}
                    value={{
                      label: currencyConfig.secondaryCurrencyName,
                      value: currencyConfig.secondaryCurrencyCode,
                    }}
                    disabled={!edit_currency_settings}
                    size='large'
                    notFoundContent={
                      <span style={{ color: '#000' }}>
                        {getI18n('salesChannelCurrency.index.nosecondary')}
                      </span>
                    }
                  />
                )}
              </ItemWrapper>
              <Nav className={styles.wrapperNav} />
            </div>
          </SectionWrapper>
          <SectionWrapper
            className={styles.wrapper}
            containerClassName={styles.formatWrapperContainer}
            title={getI18n('salesChannelCurrency.index.currencyFormat')}
            subtitle={getI18n(
              'salesChannelCurrency.index.currencyFormatSubtitle',
            )}>
            <div className={styles.warpperBody}>
              <ItemWrapper
                containerClassName={styles.formatWrapperTop}
                title={
                  <div>
                    <span>
                      {getI18n('salesChannelCurrency.index.decimalPlaces')}
                    </span>
                    <span style={{ marginLeft: '12px', fontWeight: 400 }}>
                      ( 0 - 9 )
                    </span>
                  </div>
                }>
                <Select
                  className={styles.select}
                  size='large'
                  onChange={handleChangeDecimalPosition}
                  value={currencyConfig.decimalPosition}
                  options={decimalPositionOptions}
                  disabled={!edit_currency_settings}
                />
                {
                  // (reportingCurrency?.code !== currencyConfig.currencyCode ||
                  //   (reportingCurrency?.code !==
                  //     currencyConfig.secondaryCurrencyCode &&
                  //     !!currencyConfig.secondaryCurrencyCode)) // 修改-> 必须要有的field
                  true && (
                    <ItemWrapper
                      containerClassName={styles.bottomItemWrapper}
                      title={getI18n(
                        'salesChannelCurrency.index.conversionModeTitle',
                      )}
                      bottomTitle={getI18n(
                        'salesChannelCurrency.index.conversionModeBottomTitle',
                      )}>
                      <Radio.Group
                        value={currencyConfig.conversionMode}
                        onChange={handleChangePropsViaEvent.bind(
                          this,
                          'conversionMode',
                        )}
                        disabled={!edit_currency_settings}>
                        <Space direction='vertical' className={styles.space}>
                          <div className={styles.item}>
                            <Radio value={Cnversion.Round}>
                              {getI18n(
                                'salesChannelCurrency.index.roundnumbersdown',
                              )}
                            </Radio>
                            <div className={styles.body}>
                              <div className={styles.desc}>
                                {getI18n(
                                  'salesChannelCurrency.index.roundnumbersdowntip',
                                )}
                              </div>
                              {currencyConfig.conversionMode ===
                                Cnversion.Round && (
                                <>
                                  <Select
                                    className={styles.select}
                                    size='large'
                                    value={currencyConfig.roundingRule}
                                    options={conversionOptions({
                                      getI18n,
                                    }).slice(
                                      0,
                                      currencyConfig.decimalPosition + 1,
                                    )}
                                    onChange={handleChangePropsViaValue.bind(
                                      this,
                                      'roundingRule',
                                    )}
                                    disabled={!edit_currency_settings}
                                  />
                                  <span className={styles.exmaple}>
                                    {renderRoundingExample()}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className={styles.item}>
                            <Radio
                              value={Cnversion.Truncate}
                              disabled={!edit_currency_settings}>
                              {getI18n('salesChannelCurrency.index.truncating')}
                            </Radio>
                            <div className={styles.body}>
                              <div className={styles.desc}>
                                {getI18n(
                                  'salesChannelCurrency.index.truncatingTip',
                                )}
                              </div>
                              {currencyConfig.conversionMode ===
                                Cnversion.Truncate && (
                                <>
                                  <Select
                                    className={styles.select}
                                    size='large'
                                    value={currencyConfig.truncateRule}
                                    options={conversionOptions({
                                      getI18n,
                                    }).slice(
                                      0,
                                      currencyConfig.decimalPosition + 1,
                                    )}
                                    onChange={handleChangePropsViaValue.bind(
                                      this,
                                      'truncateRule',
                                    )}
                                    disabled={!edit_currency_settings}
                                  />
                                  <span className={styles.exmaple}>
                                    {renderTruncatingExample()}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </Space>
                      </Radio.Group>
                    </ItemWrapper>
                  )
                }
              </ItemWrapper>
              <ItemWrapper containerClassName={styles.formatWrapper}>
                <div className={styles.item}>
                  <div className={styles.title}>
                    {getI18n('salesChannelCurrency.index.decimalSeparator')}
                  </div>
                  <Radio.Group
                    value={currencyConfig.decimalSeparator}
                    onChange={handleChangePropsViaEvent.bind(
                      this,
                      'decimalSeparator',
                    )}
                    disabled={!edit_currency_settings}>
                    <Space direction={'vertical'}>
                      <Radio value={DecimalSeparator.POINT}>
                        {getI18n('salesChannelCurrency.index.decimalpoint')} (.)
                      </Radio>
                      <Radio value={DecimalSeparator.COMMA}>
                        {getI18n('salesChannelCurrency.index.decimalcomma')} (,)
                      </Radio>
                    </Space>
                  </Radio.Group>
                </div>
                <div className={styles.item}>
                  <div className={styles.title}>
                    {getI18n('salesChannelCurrency.index.thousandsSeparator')}
                  </div>
                  <Radio.Group
                    value={currencyConfig.thousandsSeparator}
                    onChange={handleChangePropsViaEvent.bind(
                      this,
                      'thousandsSeparator',
                    )}
                    disabled={!edit_currency_settings}>
                    <Space direction={'vertical'}>
                      <Radio value={ThousandsSeparator.NOT_USE}>
                        {getI18n('salesChannelCurrency.index.notuse')}
                      </Radio>
                      <Radio value={ThousandsSeparator.COMMA}>
                        {getI18n('salesChannelCurrency.index.comma')} (,)
                      </Radio>
                      <Radio value={ThousandsSeparator.POINT}>
                        {getI18n('salesChannelCurrency.index.point')} (.)
                      </Radio>
                      <Radio value={ThousandsSeparator.SPACE}>
                        {getI18n('salesChannelCurrency.index.space')}
                      </Radio>
                    </Space>
                  </Radio.Group>
                </div>
              </ItemWrapper>
              <ItemWrapper containerClassName={styles.formatWrapperBottom}>
                <div className={styles.display}>
                  <div className={styles.item}>
                    <div className={styles.title}>
                      {getI18n(
                        'salesChannelCurrency.index.currencySymbolDisplayPrimaryCurrency',
                      )}
                    </div>
                    <div className={styles.body}>
                      <Radio.Group
                        value={currencyConfig.currencyDisplayFormat}
                        onChange={handleChangePropsViaEvent.bind(
                          this,
                          'currencyDisplayFormat',
                        )}
                        disabled={!edit_currency_settings}>
                        <Space direction={'vertical'}>
                          <div className={styles.radio}>
                            <Radio value={DisplayFormat.PREFIX_AMOUNT}>
                              {getI18n(
                                'salesChannelCurrency.index.prefixAmount',
                              )}
                            </Radio>
                            {currencyConfig.currencyDisplayFormat ===
                              DisplayFormat.PREFIX_AMOUNT && (
                              <div className={styles.option}>
                                <Select
                                  onChange={handleChangePropsViaValue.bind(
                                    this,
                                    'currencySymbol',
                                  )}
                                  value={currencyConfig.currencySymbol}
                                  options={labelify(primaryCuSymbol)}
                                  className={styles.select}
                                  size={'large'}
                                  disabled={!edit_currency_settings}></Select>
                                <span className={styles.exmaple}>
                                  {`${getI18n(
                                    'salesChannelCurrency.index.eg',
                                  )} ${formatExample(
                                    DisplayFormat.PREFIX_AMOUNT,
                                    currencyConfig.currencySymbol,
                                  )}`}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className={styles.radio}>
                            <Radio
                              value={DisplayFormat.AMOUNT_SUFFIX}
                              disabled={!edit_currency_settings}>
                              {getI18n(
                                'salesChannelCurrency.index.amountSuffix',
                              )}
                            </Radio>
                            {currencyConfig.currencyDisplayFormat ===
                              DisplayFormat.AMOUNT_SUFFIX && (
                              <div className={styles.option}>
                                <Select
                                  onChange={handleChangePropsViaValue.bind(
                                    this,
                                    'currencySymbol',
                                  )}
                                  value={currencyConfig.currencySymbol}
                                  options={labelify(primaryCuSymbol)}
                                  className={styles.select}
                                  size={'large'}
                                  disabled={!edit_currency_settings}></Select>
                                <span className={styles.exmaple}>
                                  {`${getI18n(
                                    'salesChannelCurrency.index.eg',
                                  )} ${formatExample(
                                    DisplayFormat.AMOUNT_SUFFIX,
                                    currencyConfig.currencySymbol,
                                  )}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </Space>
                      </Radio.Group>
                    </div>
                  </div>
                  <div className={styles.item}>
                    <div className={styles.title}>
                      {getI18n(
                        'salesChannelCurrency.index.currencySymbolDisplaySecondaryCurrency',
                      )}
                    </div>
                    <div className={styles.body}>
                      {currencyConfig.showSecondary &&
                      currencyConfig.secondaryCurrencyCode ? (
                        <Radio.Group
                          value={currencyConfig.secondaryCurrencyDisplayFormat}
                          onChange={handleChangePropsViaEvent.bind(
                            this,
                            'secondaryCurrencyDisplayFormat',
                          )}
                          disabled={!edit_currency_settings}>
                          <Space direction={'vertical'}>
                            <div className={styles.radio}>
                              <Radio
                                value={DisplayFormat.PREFIX_AMOUNT}
                                disabled={!edit_currency_settings}>
                                {getI18n(
                                  'salesChannelCurrency.index.prefixAmount',
                                )}
                              </Radio>
                              {currencyConfig.secondaryCurrencyDisplayFormat ===
                                DisplayFormat.PREFIX_AMOUNT && (
                                <div className={styles.option}>
                                  <Select
                                    onChange={handleChangePropsViaValue.bind(
                                      this,
                                      'secondaryCurrencySymbol',
                                    )}
                                    value={
                                      currencyConfig.secondaryCurrencySymbol
                                    }
                                    options={labelify(secondCuSymbol)}
                                    className={styles.select}
                                    size={'large'}
                                    disabled={!edit_currency_settings}></Select>
                                  <span className={styles.exmaple}>
                                    {`${getI18n(
                                      'salesChannelCurrency.index.eg',
                                    )} ${formatExample(
                                      DisplayFormat.PREFIX_AMOUNT,
                                      currencyConfig.secondaryCurrencySymbol,
                                    )}`}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className={styles.radio}>
                              <Radio
                                value={DisplayFormat.AMOUNT_SUFFIX}
                                disabled={!edit_currency_settings}>
                                {getI18n(
                                  'salesChannelCurrency.index.amountSuffix',
                                )}
                              </Radio>
                              {currencyConfig.secondaryCurrencyDisplayFormat ===
                                DisplayFormat.AMOUNT_SUFFIX && (
                                <div className={styles.option}>
                                  <Select
                                    onChange={handleChangePropsViaValue.bind(
                                      this,
                                      'secondaryCurrencySymbol',
                                    )}
                                    value={
                                      currencyConfig.secondaryCurrencySymbol
                                    }
                                    options={labelify(secondCuSymbol)}
                                    className={styles.select}
                                    size={'large'}
                                    disabled={!edit_currency_settings}></Select>
                                  <span className={styles.exmaple}>
                                    {`${getI18n(
                                      'salesChannelCurrency.index.eg',
                                    )} ${formatExample(
                                      DisplayFormat.AMOUNT_SUFFIX,
                                      currencyConfig.secondaryCurrencySymbol,
                                    )}`}
                                  </span>
                                </div>
                              )}
                            </div>
                          </Space>
                        </Radio.Group>
                      ) : (
                        <span>
                          {getI18n(
                            'salesChannelCurrency.index.showSecondaryTip',
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </ItemWrapper>
            </div>
          </SectionWrapper>
          <RouteBlock
            onConfirm={(f) => f()}
            okText={getI18nFromGeneral('general.commonHeader.discard_text')}
            active={!saveDisabled}
            title={getI18nFromGeneral('general.commonHeader.discard_header')}
            content={getI18nFromGeneral('general.commonHeader.leave_content')}
          />
        </div>
      </div>
    </ConfigProvider>
  ) : (
    <NoPermission />
  );
}

export default SalesChannelCurrency;

const Hint = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  return (
    <div className={styles.hint}>
      <div className={styles.hintTitle}>
        {getI18n('salesChannelCurrency.index.salesChannelCurrency')}
      </div>
      <p className={styles.paragraph}>
        {getI18n('salesChannelCurrency.index.salesChannelCurrencyTip1')}
      </p>
      <p className={styles.paragraph}>
        {getI18n('salesChannelCurrency.index.salesChannelCurrencyTip2')}
      </p>
    </div>
  );
};

const Nav = ({ className }: { className: string }) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  return (
    <div className={classnames(styles.nav, className)}>
      <Icon type='iconinfo2' className={styles.info} />
      <span>
        {`${getI18n('salesChannelCurrency.index.gotostoreSettings')} >`}&nbsp;
      </span>
      <Link to={'/settings/currency'} className={styles.link}>
        {getI18n('salesChannelCurrency.index.currency')}
      </Link>
      <span>
        &nbsp;{getI18n('salesChannelCurrency.index.gotostoreSettings2')}
      </span>
    </div>
  );
};
